/**
 * spendShare.ts — pure helpers behind the Reports "By agent" spend scoreboard.
 *
 * The Reports page shows how the window's model spend splits across agents. A
 * flat "$0.81 / $0.54" table tells a founder the amounts but not the shape —
 * who's actually eating the budget. These helpers turn the API's by-agent list
 * into share-of-total rows so the UI can draw a bar + percentage per agent.
 *
 * Honesty rule: a real, non-zero slice must never render as "0%". A founder
 * reading "0%" next to an agent that genuinely spent money would distrust the
 * whole ledger — so anything above zero but below one percent reads "<1%".
 */
import type { AgentId } from '@shared/types';

export interface AgentSpend {
  agent_id: AgentId | null;
  usd: number;
}

export interface AgentSpendRow extends AgentSpend {
  /** fraction of the window's total spend, clamped to [0, 1] */
  share: number;
  /** founder-readable percent: "53%", "<1%" for a real-but-tiny slice, "0%" only when genuinely zero */
  pct: string;
}

/** Percent label that never rounds a real, non-zero slice down to "0%". */
export function pctLabel(share: number): string {
  if (!(share > 0)) return '0%';
  const p = share * 100;
  if (p < 1) return '<1%';
  return `${Math.round(p)}%`;
}

/**
 * Turn a spend-by-agent list into share-of-total rows for the scoreboard.
 * `total` is the window total the API already computed; if it's missing or zero
 * we fall back to summing the rows so a stale/absent total never blanks every
 * bar. Negative or non-finite spends are floored to 0 — spend can't be negative.
 */
export function spendShares(rows: AgentSpend[], total?: number | null): AgentSpendRow[] {
  const safe = (n: number) => (Number.isFinite(n) && n > 0 ? n : 0);
  const denom =
    total != null && Number.isFinite(total) && total > 0
      ? total
      : rows.reduce((s, r) => s + safe(r.usd), 0);
  return rows.map((r) => {
    const share = denom > 0 ? safe(r.usd) / denom : 0;
    return { ...r, share, pct: pctLabel(share) };
  });
}
